import React, { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { apiGet } from "@/lib/api";
import { LoadingBlock } from "@/components/Spinner";
import Modal from "@/components/Modal";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export default function CalendarView({ onClose }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [dailyPnl, setDailyPnl] = useState({});
  const [monthlySummary, setMonthlySummary] = useState({});
  const [currency, setCurrency] = useState("USDT");
  const [loading, setLoading] = useState(true);
  const [allTime, setAllTime] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const data = await apiGet("/api/trade/pnl/daily?months=6");
      if (data && data.success) {
        setDailyPnl(data.daily_pnl || {});
        setMonthlySummary(data.monthly_summary || {});
        setCurrency(data.currency || "USDT");
        if (data.all_time_pnl) setAllTime(data.all_time_pnl);
      }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const monthKey = `${year}-${String(month + 1).padStart(2, "0")}`;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstRaw = new Date(year, month, 1).getDay();
  const firstDay = firstRaw === 0 ? 6 : firstRaw - 1;
  const now = new Date();
  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth();
  const dateKey = (d) => `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

  const totalPnl = allTime ? allTime.net_pnl : Object.values(monthlySummary).reduce((s, m) => s + m.net_pnl, 0);
  const totalBets = allTime ? allTime.total_bets : Object.values(monthlySummary).reduce((s, m) => s + m.bets, 0);
  const totalWins = allTime ? allTime.wins : Object.values(monthlySummary).reduce((s, m) => s + m.wins, 0);
  const totalLosses = allTime ? allTime.losses : Object.values(monthlySummary).reduce((s, m) => s + m.losses, 0);
  const cms = monthlySummary[monthKey];

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(<div key={`e${i}`} />);
  for (let d = 1; d <= daysInMonth; d++) {
    const dk = dateKey(d);
    const dd = dailyPnl[dk];
    const today = isCurrentMonth && d === now.getDate();
    let bg = "transparent", color = "#6b7280", text = "";
    if (dd) {
      if (dd.net_pnl > 0) { bg = "#10b98118"; color = "#10b981"; text = `+${dd.net_pnl.toFixed(1)}`; }
      else if (dd.net_pnl < 0) { bg = "#ef444418"; color = "#ef4444"; text = dd.net_pnl.toFixed(1); }
      else if (dd.pending > 0) { bg = "#f59e0b18"; color = "#f59e0b"; text = "Open"; }
      else { bg = "#6b728018"; text = "0.0"; }
    }
    cells.push(
      <button key={`d${d}`} onClick={() => dd && setSelectedDay(dk)} disabled={!dd}
        className={`min-h-[56px] rounded-lg flex flex-col items-center justify-center p-1 ${dd ? "border border-[#334155] cursor-pointer" : ""} ${today ? "!border-2 !border-[#3b82f6]" : ""}`}
        style={{ backgroundColor: bg }}>
        <span className={`text-sm font-semibold ${today ? "text-[#3b82f6]" : "text-[#e2e8f0]"}`}>{d}</span>
        {dd ? (<><span className="text-[10px] font-bold mt-0.5" style={{ color }}>{text}</span>
          <span className="flex gap-0.5 mt-0.5">{dd.wins > 0 && <span className="w-1 h-1 rounded-full bg-[#10b981]" />}{dd.losses > 0 && <span className="w-1 h-1 rounded-full bg-[#ef4444]" />}{dd.pending > 0 && <span className="w-1 h-1 rounded-full bg-[#f59e0b]" />}</span></>) : <span className="h-5" />}
      </button>
    );
  }

  const dayData = selectedDay ? dailyPnl[selectedDay] : null;

  return (
    <div className="bg-[#0f172a]">
      <div className="flex items-center justify-between px-1 py-1 mb-4">
        <h2 className="text-xl font-bold text-[#e2e8f0]">P&L Calendar</h2>
        {onClose && <button onClick={onClose} className="text-[#9ca3af]" data-testid="calendar-close"><X size={22} /></button>}
      </div>

      {loading ? <LoadingBlock message="Loading P&L data…" /> : (
        <>
          <div className="rounded-2xl p-5 mb-4 border-2 text-center" style={{ backgroundColor: totalPnl >= 0 ? "#10b98110" : "#ef444410", borderColor: totalPnl >= 0 ? "#10b981" : "#ef4444" }}>
            <p className="text-[13px] text-[#9ca3af] font-semibold mb-1">All-Time P&L</p>
            <p className="text-3xl font-bold mb-3" style={{ color: totalPnl >= 0 ? "#10b981" : "#ef4444" }}>{totalPnl >= 0 ? "+" : ""}{totalPnl.toFixed(2)} {currency}</p>
            <div className="flex bg-[#0f172a]/50 rounded-xl p-3">
              {[["Total", totalBets, "#e2e8f0"], ["Wins", totalWins, "#10b981"], ["Losses", totalLosses, "#ef4444"], ["Win Rate", `${totalWins + totalLosses > 0 ? ((totalWins / (totalWins + totalLosses)) * 100).toFixed(0) : 0}%`, "#f59e0b"]].map(([l, v, c]) => (
                <div key={l} className="flex-1 text-center"><p className="text-lg font-bold" style={{ color: c }}>{v}</p><p className="text-[10px] text-[#6b7280] mt-0.5">{l}</p></div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between mb-2">
            <button onClick={() => setCurrentDate(new Date(year, month - 1, 1))} className="w-11 h-11 rounded-full bg-[#1e293b] flex items-center justify-center"><ChevronLeft size={26} color="#e2e8f0" /></button>
            <button onClick={() => setCurrentDate(new Date())} className="text-center"><p className="text-xl font-bold text-[#e2e8f0]">{MONTHS[month]} {year}</p>{!isCurrentMonth && <p className="text-[12px] text-[#10b981]">Go to today</p>}</button>
            <button onClick={() => { if (!isCurrentMonth) setCurrentDate(new Date(year, month + 1, 1)); }} disabled={isCurrentMonth} className={`w-11 h-11 rounded-full bg-[#1e293b] flex items-center justify-center ${isCurrentMonth ? "opacity-30" : ""}`}><ChevronRight size={26} color="#e2e8f0" /></button>
          </div>

          {cms && (
            <div className="flex bg-[#1e293b] rounded-xl p-3.5 mb-3">
              {[["Month P&L", `${cms.net_pnl >= 0 ? "+" : ""}${cms.net_pnl.toFixed(2)}`, cms.net_pnl >= 0 ? "#10b981" : "#ef4444"], ["Bets", cms.bets, "#e2e8f0"], ["W", cms.wins, "#10b981"], ["L", cms.losses, "#ef4444"]].map(([l, v, c]) => (
                <div key={l} className="flex-1 text-center"><p className="text-base font-bold" style={{ color: c }}>{v}</p><p className="text-[10px] text-[#6b7280] mt-0.5">{l}</p></div>
              ))}
            </div>
          )}

          <div className="bg-[#1e293b] rounded-2xl p-2 mb-3">
            <div className="grid grid-cols-7 mb-1">{WEEKDAYS.map((d) => <div key={d} className="text-center text-[12px] text-[#6b7280] font-semibold py-2">{d}</div>)}</div>
            <div className="grid grid-cols-7 gap-1">{cells}</div>
          </div>

          <div className="flex items-center justify-center gap-4 py-2 text-[12px] text-[#9ca3af]">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#10b981]" />Profit</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#ef4444]" />Loss</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#f59e0b]" />Open</span>
            <span className="text-[11px] text-[#4b5563] italic">Tap a day for details</span>
          </div>
        </>
      )}

      <Modal open={!!selectedDay} onClose={() => setSelectedDay(null)} title={selectedDay ? new Date(...selectedDay.split("-").map((v, i) => i === 1 ? parseInt(v) - 1 : parseInt(v))).toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" }) : ""} testId="calendar-day-modal">
        {dayData && (
          <div>
            <div className="rounded-xl p-4 mb-4 text-center border" style={{ backgroundColor: dayData.net_pnl >= 0 ? "#10b98110" : "#ef444410", borderColor: dayData.net_pnl >= 0 ? "#10b98140" : "#ef444440" }}>
              <p className="text-2xl font-bold mb-3" style={{ color: dayData.net_pnl >= 0 ? "#10b981" : "#ef4444" }}>{dayData.net_pnl >= 0 ? "+" : ""}{dayData.net_pnl.toFixed(2)} {currency}</p>
              <div className="flex">
                {[["Staked", dayData.total_staked.toFixed(2), "#e2e8f0"], ["Wins", dayData.wins, "#10b981"], ["Losses", dayData.losses, "#ef4444"]].concat(dayData.pending > 0 ? [["Open", dayData.pending, "#f59e0b"]] : []).map(([l, v, c]) => (
                  <div key={l} className="flex-1 text-center"><p className="text-lg font-bold" style={{ color: c }}>{v}</p><p className="text-[10px] text-[#6b7280]">{l}</p></div>
                ))}
              </div>
            </div>
            <div className="max-h-72 overflow-y-auto space-y-2">
              {(dayData.bets || []).map((bet, i) => (
                <div key={bet.reference_id || `${bet.event_name || "bet"}-${i}`} className="bg-[#0f172a] rounded-lg p-3" style={{ borderLeft: `3px solid ${bet.status === "WIN" ? "#10b981" : bet.status === "ACCEPTED" ? "#f59e0b" : "#ef4444"}` }}>
                  <div className="flex justify-between items-center mb-1.5"><span className="text-sm text-[#e2e8f0] font-semibold truncate mr-2">{bet.event_name}</span><span className="text-[10px] font-bold text-white px-2 py-0.5 rounded" style={{ backgroundColor: bet.status === "WIN" ? "#10b98130" : bet.status === "ACCEPTED" ? "#f59e0b30" : "#ef444430" }}>{bet.status === "ACCEPTED" ? "OPEN" : bet.status}</span></div>
                  <div className="flex gap-3 text-[12px] text-[#9ca3af]"><span>Stake: {bet.stake.toFixed(2)}</span><span>Odds: {bet.odds.toFixed(3)}x</span><span className="font-bold" style={{ color: bet.profit > 0 ? "#10b981" : bet.profit < 0 ? "#ef4444" : "#f59e0b" }}>{bet.profit > 0 ? "+" : ""}{bet.profit.toFixed(2)}</span></div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
